import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import styles from '../Billing.module.css';
import SearchFilter from '../SearchFilter';
import FilterModal from './FilterModal';
import { ConfirmationModal } from '../../../components/confirmationModal';
import CustomPagination from '../../../components/common/CustomPagination';
import TableSkeleton from '../../../components/common/TableSkeleton';
import { getCounselingPatientList, PackageVisit, PackageVisitResponse, buildCounselingQuery, mapStatusToChip, mapStatusToAction, formatGBDate, getStatusCounts } from './package.helper';
import { ClosePackageCell } from './ClosePackageCell';
import { getRequest } from '../../../services/httpService';
import { FilterValues } from '../billing.helper';
import StatusChip from './StatusChip';
import { useAppQuery } from '../../../services/appQuery';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { storePrevRoute } from '../../../utils/commonUtils';
import { useCounselingStore, usePatientPackageStore } from '../store';

// Main component
const Package: React.FC = () => {
  // State
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const { t } = useTranslation('Packagebilling');
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = useState(10);
  const setCounselingSessionId = useCounselingStore(state => state.setCounselingSessionId);
  const setPatientPackageId = usePatientPackageStore(state => state.setPatientPackageId);
  const [closePackageModalOpen, setClosePackageModalOpen] = useState(false);
  const [selectedPackageRow, setSelectedPackageRow] = useState<PackageVisit | null>(null);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);


  // Build query string for API via helper
  const counselingQuery = buildCounselingQuery({
    page,
    pageSize,
    filters: filterValues as unknown as Record<string, unknown>,
    searchTerm,
  });

  // Fetch data
  const fetchCounselingPatients = useCallback(() => {
    const url = getCounselingPatientList(counselingQuery);
    return getRequest<PackageVisitResponse>(url);
  }, [counselingQuery]);

  const {
    data: counselingPatients,
    error: counselingPatientsError,
    refetch: refetchCounselingPatients,
    isLoading,
  } = useAppQuery<PackageVisitResponse>(
    ['counselingPatients', counselingQuery],
    fetchCounselingPatients,
    {
      staleTime: 0,
      gcTime: 0,
      debounceMs: 300
    }
  );
  // Refetch when navigated back with refetch flag from details page
  useEffect(() => {
    const state = (location as unknown as { state?: { refetch?: boolean; ts?: number } }).state;
    if (state?.refetch) {
      refetchCounselingPatients();
    }
  }, [location, refetchCounselingPatients]);
  // Error logging
  useEffect(() => {
    if (counselingPatientsError) {
      // eslint-disable-next-line no-console
      console.error('Error loading counseling patients:', counselingPatientsError);
    }
  }, [counselingPatientsError]);

  // Handlers
  const handleActionClick = (action: 'view' | 'edit' | 'add', row: PackageVisit) => {
    storePrevRoute(location.pathname, location.search);
    // Store counseling_session_id in Zustand store
    setCounselingSessionId(row.counseling_session_id ?? null);
    // Store package_id in Zustand store
    setPatientPackageId(row.package_id ?? null);
    switch (action) {
    case 'view':
      navigate(`/admin/billing/package-details/${row.patient}`, { state: { packageVisit: row, mode:'view' } });
      break;
    case 'edit':
      navigate(`/admin/billing/package-details/${row.patient}`, { state: { packageVisit: row , mode:'edit'} });
      // navigate to edit route when implemented
      break;
    case 'add':
      navigate(`/admin/billing/package-details/${row.patient}`, { state: { packageVisit: row , mode:'add'} });
      break;
    default:
      break;
    }
  };

  const handleClickFilter = () => setFilterOpen(true);

  const handleFilterApply = (values: FilterValues) => {
    setFilterValues(values);
    setPage(0);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage);

  const handleStatusChipClick = (status: string) => {
    setSelectedStatus(prev => {
      const newStatus = prev === status ? null : status;
      setFilterValues(current => {
        const updated = { ...current };
        if (newStatus) updated.status = newStatus;
        else delete updated.status;
        return updated;
      });
      setPage(0);
      return newStatus;
    });
  };

  const handleClosePackage = (e: React.MouseEvent, row: PackageVisit) => {
    e.stopPropagation(); // Prevent row click navigation
    setSelectedPackageRow(row);
    setClosePackageModalOpen(true);
  };

  const handleClosePackageConfirm = () => {
    if (!selectedPackageRow) return;
    
    // If there are unsettled invoices, redirect to patient invoice page to settle them first
    if (selectedPackageRow.has_unsettled_package_invoice) {
      setClosePackageModalOpen(false);
      storePrevRoute(location.pathname, location.search);
      navigate(`/admin/billing/invoice/patient-invoice/${selectedPackageRow.patient}`);
      return;
    }
    
    setClosePackageModalOpen(false);
    storePrevRoute(location.pathname, location.search);
    // Navigate to invoice form with package close mode
    navigate(`/admin/billing/invoice/new/${selectedPackageRow.patient}`, {
      state: {
        package_id: selectedPackageRow.package_id,
        bill_type: 'Package',
        counseling_session_id: selectedPackageRow.counseling_session_id,
        can_close_package_invoice: selectedPackageRow.can_close_package_invoice,
        mode: 'close_package'
      }
    });
  };

  // Table row rendering
  const renderTableRows = () => {
    if (counselingPatientsError) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" style={{ color: 'red' }}>
            {t('error_loading_data')}
          </TableCell>
        </TableRow>
      );
    }
    if (counselingPatients?.objects.length) {
      return counselingPatients.objects.map((row, idx) => (
        <TableRow key={idx} data-testid={`package-row-${idx}`} hover  onClick={() => handleActionClick(mapStatusToAction(row.status), row)} style={{ cursor: 'pointer' }}>
          <TableCell>{formatGBDate(row.visit_date)}</TableCell>
          <TableCell>
            {row.patient_name}
            <br />
            <span style={{ fontSize: 12, color: '#888' }}>{row.patient_mrn_number} | {row.patient_gender}</span>
          </TableCell>
          <TableCell>{row.contact_number}</TableCell>
          <TableCell align="center">{row.treating_doctor ?? '-'}</TableCell>
          <TableCell align="center">{row.treatment_advice ?? '-'}</TableCell>
          <TableCell align="center">{row.counselor_name ?? '-'}</TableCell>
          <TableCell align="center">
            {(() => {
              const m = mapStatusToChip(row.status);
              return <StatusChip name={m.name} label={t(m.tKey)} />;
            })()}
          </TableCell>
          <ClosePackageCell row={row} onClosePackage={handleClosePackage} />
        </TableRow>
      ));
    }
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">{t('no_data_available')}</TableCell>
      </TableRow>
    );
  };

  // Status summary rendering
  const renderStatusSummary = () => {
    const { todo: todoCount, completed: completedCount, inProgress: incompleteCount } = getStatusCounts(counselingPatients?.meta);
    return (
      <>
        <StatusChip name="todo" label={t('to_do')} count={todoCount} selected={selectedStatus === 'todo'} onClick={handleStatusChipClick} />
        <StatusChip name="completed" label={t('completed')} count={completedCount} selected={selectedStatus === 'completed'} onClick={handleStatusChipClick} />
        <StatusChip name="InProgress" label={t('incomplete')} count={incompleteCount} selected={selectedStatus === 'InProgress'} onClick={handleStatusChipClick} />
      </>
    );
  };

  // Main render
  return (
    <Box data-testid="billing-package-list-container">
      {/* Header and Filters */}
      <Box data-testid="package-header-section" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <p className={styles.TableTitle}>
            {t('counselling_patient_list')} ({counselingPatients?.meta.total_count || 0})
          </p>
        </Box>
        <SearchFilter searchTerm={searchTerm} setSearchTerm={setSearchTerm} billType={'Pakage'} onFilterClick={handleClickFilter} />
        <FilterModal open={filterOpen} onClose={() => setFilterOpen(false)} onApply={handleFilterApply} />
      </Box>

      {/* Table */}
      <Box data-testid="package-table-section" sx={{ mt: 3 }}>
        <TableContainer component={Paper} elevation={0} sx={{ width: '100%', border: 'none', padding: 0 }}>
          <Table data-testid="package-table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell><strong>{t('visit_date')}</strong></TableCell>
                <TableCell><strong>{t('patient_name')}</strong></TableCell>
                <TableCell><strong>{t('contact_no')}</strong></TableCell>
                <TableCell align="center"><strong>{t('treatment_doctor')}</strong></TableCell>
                <TableCell align="center"><strong>{t('treatment_advice')}</strong></TableCell>
                <TableCell align="center"><strong>{t('counsellor_name')}</strong></TableCell>
                <TableCell align="center"><strong>{t('status')}</strong></TableCell>
                <TableCell align="center"><strong>{t('action')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableSkeleton
                  columnWidths={[140, 260, 160, 180, 220, 180, 140]}
                  rows={pageSize}
                />
              ) : (
                renderTableRows()
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Status Summary */}
        <Box data-testid="package-status-summary" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mt: 3 }}>
          {renderStatusSummary()}
        </Box>

        {/* Pagination */}
        <Box data-testid="package-pagination">
          <CustomPagination
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[10, 15, 25, 50]}
            onPageSizeChange={handlePageSizeChange}
            totalCount={counselingPatients?.meta.total_count || 0}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>

      {/* Close Package Confirmation Modal */}
      <ConfirmationModal
        open={closePackageModalOpen}
        handleClose={() => setClosePackageModalOpen(false)}
        onConfirm={handleClosePackageConfirm}
        type="warning"
        title={
          selectedPackageRow?.has_unsettled_package_invoice
            ? t('unsettled_invoice_warning_title') || 'Please Settle Pending Bills'
            : t('close_package_confirmation_title')
        }
        contentMessage={
          selectedPackageRow?.has_unsettled_package_invoice
            ? (
              <>
                {t('close_package_unsettled_invoice_line1') || 'This package cannot be closed until all pending bills are settled.'}
                <br />
                {t('close_package_unsettled_invoice_line2') || 'Please review and settle outstanding invoices to proceed.'}
              </>
            )
            : t('close_package_confirmation_message')
        }
        confirmText={
          selectedPackageRow?.has_unsettled_package_invoice
            ? t('settle_unsettled_bills') || 'Settle Bills Now'
            : t('confirm')
        }
        cancelText={selectedPackageRow?.has_unsettled_package_invoice ? undefined : t('cancel')}
        cancelButtonProps={
          selectedPackageRow?.has_unsettled_package_invoice
            ? { sx: { display: 'none' } }
            : undefined
        }
        confirmButtonProps={{
          variant: selectedPackageRow?.has_unsettled_package_invoice ? 'contained' : 'outlined',
          sx: selectedPackageRow?.has_unsettled_package_invoice
            ? {
              backgroundColor: '#505050',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#404040',
              },
              minWidth: '220px',
            }
            : undefined,
        }}
        centerActions={selectedPackageRow?.has_unsettled_package_invoice}
        showCloseButton={selectedPackageRow?.has_unsettled_package_invoice}
      />
    </Box>
  );
};

export default Package;